import type {
  ProjectContextResponse,
  ProjectContextVersionDetailResponse,
  ProjectContextVersionsResponse,
  ProjectDetailResponse,
  ProjectOverviewResponse,
  ProjectsResponse,
  UpdateProjectAiVersionRequest,
  UpdateProjectContextRequest,
} from '@ai-meeting/shared'
import { apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'

/**
 * The Backlog projects the signed-in reader's own API key can see.
 *
 * Throws rather than swallowing, unlike the directory helper next to it: the
 * picker has a state for each answer and an error is one of them, so the
 * caller needs to be able to tell "Backlog returned nothing" from "the call
 * failed". Not being connected is neither — it arrives as `status` on a
 * successful response, because it is something the reader can go and fix.
 *
 * @throws ApiError carrying the server's message, or a generic one when the
 *   request never reached it.
 */
export async function fetchProjects(): Promise<ProjectsResponse> {
  try {
    const { data } = await apiClient.get<ProjectsResponse>('/api/projects')
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadProjects)
  }
}

/**
 * The same projects with what the Project Management cards draw: how many
 * meetings here are attached to each, and where to open it in Backlog.
 *
 * Throws on the same terms as {@link fetchProjects}, and for the same reason —
 * the screen distinguishes "Backlog has nothing for you" from "the call
 * failed", and only the second one is worth an error banner.
 *
 * @throws ApiError carrying the server's message, or a generic one when the
 *   request never reached it.
 */
export async function fetchProjectOverview(): Promise<ProjectOverviewResponse> {
  try {
    const { data } = await apiClient.get<ProjectOverviewResponse>('/api/projects/overview')
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadProjects)
  }
}

/**
 * One project's own screen.
 *
 * @param projectKey Backlog's short code; matched case-insensitively upstream.
 * @throws ApiError. A 404 is the ordinary answer for a key the reader's grant
 *   cannot see, and the page draws a not-found state for it rather than an
 *   error banner — so callers have to read `status` off the error.
 */
export async function fetchProject(projectKey: string): Promise<ProjectDetailResponse> {
  try {
    const { data } = await apiClient.get<ProjectDetailResponse>(
      `/api/projects/${encodeURIComponent(projectKey)}`
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadProjects)
  }
}

/**
 * Declared + learned project context for the Context & Dictionary tab.
 *
 * @throws ApiError when the call fails or the project is not visible (404).
 */
export async function fetchProjectContext(projectKey: string): Promise<ProjectContextResponse> {
  try {
    const { data } = await apiClient.get<ProjectContextResponse>(
      `/api/projects/${encodeURIComponent(projectKey)}/context`
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadProjectContext)
  }
}

/**
 * Replace the declared half (`text`, `general`, `terms`) only.
 *
 * @throws ApiError on validation failure or when the project is not visible.
 */
export async function updateProjectContext(
  projectKey: string,
  body: UpdateProjectContextRequest
): Promise<ProjectContextResponse> {
  try {
    const { data } = await apiClient.put<ProjectContextResponse>(
      `/api/projects/${encodeURIComponent(projectKey)}/context`,
      body
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSaveProjectContext)
  }
}

/**
 * Correct the AI version (`learnedText`) and file a `human` version upstream.
 *
 * @throws ApiError on validation failure or when the project is not visible.
 */
export async function updateProjectAiVersion(
  projectKey: string,
  body: UpdateProjectAiVersionRequest
): Promise<ProjectContextResponse> {
  try {
    const { data } = await apiClient.put<ProjectContextResponse>(
      `/api/projects/${encodeURIComponent(projectKey)}/context/ai-version`,
      body
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSaveProjectAiVersion)
  }
}

/**
 * Context description history for the versions card (newest first).
 *
 * @throws ApiError when the call fails or the project is not visible (404).
 */
export async function fetchProjectContextVersions(
  projectKey: string
): Promise<ProjectContextVersionsResponse> {
  try {
    const { data } = await apiClient.get<ProjectContextVersionsResponse>(
      `/api/projects/${encodeURIComponent(projectKey)}/context/versions`
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadProjectContextVersions)
  }
}

/**
 * One version's metadata, diff segments, and snapshot body.
 * Meeting-sourced `text` is the AI-learned profile, not the user-written description.
 *
 * @throws ApiError when the version is missing or the project is not visible.
 */
export async function fetchProjectContextVersion(
  projectKey: string,
  versionId: string
): Promise<ProjectContextVersionDetailResponse> {
  try {
    const { data } = await apiClient.get<ProjectContextVersionDetailResponse>(
      `/api/projects/${encodeURIComponent(projectKey)}/context/versions/${encodeURIComponent(versionId)}`
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadProjectContextVersions)
  }
}

/**
 * Restore a historical declared snapshot (not a meeting-learned AI profile).
 *
 * @throws ApiError when the version is missing, not restorable, or the project is not visible.
 */
export async function restoreProjectContextVersion(
  projectKey: string,
  versionId: string
): Promise<ProjectContextResponse> {
  try {
    const { data } = await apiClient.post<ProjectContextResponse>(
      `/api/projects/${encodeURIComponent(projectKey)}/context/versions/${encodeURIComponent(versionId)}/restore`
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedRestoreProjectContextVersion)
  }
}
